import type { Scenario } from '../types';

export const MID_SCENE: Scenario = {
  id: 'mid-scene',
  title: 'mid-scene check-in',
  brief: 'you are in the middle of a negotiated scene. he has not violated the agreement, but something has shifted that you weren\'t expecting.',
  skill: 'using yellow before red, and trusting your own read in the moment',
  estMinutes: 4,
  startNode: 'open',
  nodes: {
    open: {
      id: 'open',
      paragraphs: [
        { text: 'you are in the scene. the dynamic is what you negotiated for. and somewhere in the last minute, the feeling in your chest changed.', speaker: 'narration' },
        { text: 'nothing he is doing has crossed the agreement. but the trajectory feels different than you wanted. you can feel something building that you are not sure you want at full volume.', speaker: 'narration' },
      ],
      choices: [
        { text: 'call yellow now, before the feeling clarifies.', nextNode: 'yellow' },
        { text: 'wait one more minute to see if it resolves on its own.', nextNode: 'wait' },
        { text: 'tell him plainly what you noticed without using a safeword.', nextNode: 'plain' },
      ],
    },
    yellow: {
      id: 'yellow',
      paragraphs: [
        { text: '"yellow," you say. the word is small. it doesn\'t need to be loud.', speaker: 'you' },
        { text: 'he stops immediately. the change in him is total — body softens, hands ease, his face shifts from in-scene to with-you.', speaker: 'narration' },
        { text: '"what do you need?"', speaker: 'them' },
      ],
      choices: [
        { text: '"i don\'t know yet. can we just be still for a moment."', nextNode: 'still' },
        { text: '"slower. and let me feel where you\'re going before you go."', nextNode: 'pace-down' },
        { text: '"i think i need to end this."', nextNode: 'red-from-yellow' },
      ],
    },
    wait: {
      id: 'wait',
      paragraphs: [
        { text: 'you wait. the minute extends.', speaker: 'narration' },
        { text: 'the feeling does not resolve. it gets sharper. you notice, somewhere behind everything, that you are starting to perform okay-ness in your body so he won\'t notice.', speaker: 'narration' },
        { text: 'that, you realize, is the answer.', speaker: 'narration' },
      ],
      choices: [
        { text: 'call yellow now. the discipline is in catching the performance.', nextNode: 'yellow' },
        { text: 'call red. stop everything.', nextNode: 'red' },
      ],
    },
    plain: {
      id: 'plain',
      paragraphs: [
        { text: '"hey," you say, and the word changes the shape of the room. "something shifted in me. can we slow."', speaker: 'you' },
        { text: 'he stops. he doesn\'t ask which word you meant.', speaker: 'narration' },
        { text: '"thank you for telling me. what do you need?"', speaker: 'them' },
      ],
      choices: [
        { text: '"a minute of stillness."', nextNode: 'still' },
        { text: '"i think we should end here."', nextNode: 'red-from-yellow' },
      ],
    },
    still: {
      id: 'still',
      paragraphs: [
        { text: 'he holds. not in the scene-way; in the after-way. one hand at your back, the other at the back of your neck. the room is the room again.', speaker: 'narration' },
        { text: 'after some time, the feeling in your chest sorts itself out. you find words.', speaker: 'narration' },
      ],
      choices: [
        { text: 'tell him what you actually noticed, now that you can name it.', nextNode: 'integrated' },
        { text: 'tell him you want to be done for tonight.', nextNode: 'red-from-yellow' },
      ],
    },
    'pace-down': {
      id: 'pace-down',
      paragraphs: [
        { text: '"got it." his pace changes. he checks in with his eyes between actions.', speaker: 'narration' },
        { text: 'the scene continues, but it has a different texture. you are inside it again, but you are also more here.', speaker: 'narration' },
      ],
      choices: [
        { text: 'stay in. ride the rest at the new pace.', nextNode: 'finished-well' },
        { text: 'call yellow again to slow further.', nextNode: 'still' },
      ],
    },
    'red-from-yellow': {
      id: 'red-from-yellow',
      paragraphs: [
        { text: '"okay. we stop." he steps back, gets a blanket, sits beside you instead of with you.', speaker: 'narration' },
        { text: '"you did exactly the right thing. tell me what you need now."', speaker: 'them' },
      ],
      choices: [
        { text: 'ask for water and quiet.', nextNode: 'closed-well' },
      ],
    },
    red: {
      id: 'red',
      paragraphs: [
        { text: '"red." the word does what it is supposed to do. everything stops.', speaker: 'you' },
        { text: 'he steps back. he gets a blanket. he sits beside you instead of with you.', speaker: 'narration' },
        { text: '"thank you for telling me. tell me what you need."', speaker: 'them' },
      ],
      choices: [
        { text: 'ask for water and quiet.', nextNode: 'closed-well' },
      ],
    },
    integrated: {
      id: 'integrated',
      paragraphs: [
        { text: 'you tell him what you noticed. he listens. he doesn\'t defend, he doesn\'t explain.', speaker: 'narration' },
        { text: '"thank you for catching it. let\'s keep that in mind for next time."', speaker: 'them' },
        { text: 'next time. it sounds true.', speaker: 'narration' },
      ],
    },
    'finished-well': {
      id: 'finished-well',
      paragraphs: [
        { text: 'the scene closes on a beat you both can feel. later, in aftercare, he asks you about the moment you slowed.', speaker: 'narration' },
        { text: '"that was a good catch," he says. "i was about to escalate. you saved us from a worse version of tonight."', speaker: 'them' },
      ],
    },
    'closed-well': {
      id: 'closed-well',
      paragraphs: [
        { text: 'he gets the water. he doesn\'t leave the room. when you are warm again, he asks if you want him close or want him further.', speaker: 'narration' },
        { text: 'you answer truthfully. he listens.', speaker: 'narration' },
        { text: 'the scene did not finish, but the relationship did.', speaker: 'narration' },
      ],
    },
  },
  reflection: {
    title: 'on the small word that stops everything',
    skillPracticed: 'using yellow before red — and the discipline of catching yourself before you start performing okay-ness',
    body: 'most safewords are not called because something terrible happened. they are called because something subtle shifted and the right answer is to slow, name, and re-check. the hard skill is noticing the moment your body begins to perform a yes you are no longer feeling — and catching that moment before it becomes a story you tell yourself later. yellow exists for exactly this. a partner who responds well to yellow earns the right to keep getting to play with you. one who doesn\'t — that information is also useful.',
    otherPaths: [
      'you could have used a nonverbal signal you and he had agreed on for moments when speech is hard.',
      'you could have asked him what he noticed in you — sometimes the partner is feeling the shift too, and naming it together is faster than naming it alone.',
      'you could have called yellow without explaining why. the explanation does not have to be immediate. the stop does.',
    ],
  },
};
